import { SubmitHandler } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { faBellSlash, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateContact } from 'api/contacts/single';
import { getContactFormQueryParams } from 'assets/js/pages/contacts/contactFormParams';
import * as Form from 'components/form';
import * as Tooltip from 'components/tooltip/Tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { inlineAddress } from 'helpers/address';
import { isoDateFromServer } from 'helpers/dates';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface ContactCommunicationPreferencesForm {
    allowEngageEmail: boolean;
    allowEngageSms: boolean;
    allowEngageAddress: boolean;
}

const ContactCommunicationPreferences = () => {
    const { accountId } = useCurrentAccount();
    const { data: contact } = useCurrentContact();
    const toast = useToast();
    const queryClient = useQueryClient();
    const history = useHistory();

    const methods = Form.useGivebutterForm<ContactCommunicationPreferencesForm>({
        defaultValues: {
            allowEngageEmail: contact.is_email_subscribed,
            allowEngageSms: contact.is_phone_subscribed,
            allowEngageAddress: contact.is_address_subscribed,
        },
    });

    const { mutate: savePreferences, isPending } = useMutation({
        mutationFn: (form: ContactCommunicationPreferencesForm) => {
            return updateContact(accountId, contact.id, form);
        },
        onSuccess: () => {
            toast.success('Communication preferences saved successfully');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contacts(accountId),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data.message ?? 'Failed to save communication preferences');
        },
    });

    const { control, handleSubmit, path } = methods;

    const submit = handleSubmit(
        savePreferences as SubmitHandler<ContactCommunicationPreferencesForm>,
    );

    return (
        <Box className={'flex flex-col'}>
            <div className={'flex items-center justify-between px-6 py-4'}>
                <div className={'flex items-center gap-2'}>
                    <p className={'text-lg font-medium text-gray-900'}>Communication Preferences</p>
                    <Tooltip.Root>
                        <Tooltip.Trigger
                            asChild={true}
                            tabIndex={0}
                            className={`cursor-help text-gray-500`}
                        >
                            <FontAwesomeIcon icon={faQuestionCircle} />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content inverse>
                                <p>
                                    These subscription settings are for{' '}
                                    <span className={'font-medium'}>Engage</span> messages only, and
                                    will not affect system messages such as email receipts.
                                </p>
                                <Tooltip.Arrow inverse />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            </div>
            <Separator />
            <Form.Provider {...methods}>
                <form className={'contents'} onSubmit={submit}>
                    <div className={'flex flex-col gap-3 px-6 py-3'}>
                        <div className={'flex items-center justify-between'}>
                            <div className="flex flex-col">
                                <Form.Label className="text-md" name={path('allowEngageEmail')}>
                                    Email
                                </Form.Label>
                                <div className="italic text-gray-600">
                                    {contact?.primary_email ?? (
                                        <Button
                                            variant="none"
                                            color="primary"
                                            className="font-medium"
                                            onClick={() =>
                                                history.push({
                                                    pathname: `${location.pathname}/details/edit`,
                                                    search: getContactFormQueryParams({
                                                        only: ['emails'],
                                                        autoFocus: 'emails.0.value',
                                                    }),
                                                })
                                            }
                                        >
                                            + Add Email
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <Form.Switch
                                disabled={isPending}
                                onCheckedChange={() => {
                                    submit();
                                }}
                                control={control}
                                name={'allowEngageEmail'}
                            />
                        </div>
                        <Separator className={'!-mx-6 !w-auto'} />
                        <div className={'flex items-center justify-between'}>
                            <div className="flex flex-col">
                                <Form.Label className="text-md" name={path('allowEngageSms')}>
                                    SMS
                                </Form.Label>
                                <div className="italic text-gray-600">
                                    {contact?.primary_phone ? (
                                        getFormattedPhoneNumber(contact?.primary_phone)
                                    ) : (
                                        <Button
                                            variant="none"
                                            color="primary"
                                            className="font-medium"
                                            onClick={() =>
                                                history.push({
                                                    pathname: `${location.pathname}/details/edit`,
                                                    search: getContactFormQueryParams({
                                                        only: ['phones'],
                                                        autoFocus: 'phones.0.value',
                                                    }),
                                                })
                                            }
                                        >
                                            + Add Phone
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <Form.Switch
                                disabled={isPending}
                                onCheckedChange={() => {
                                    submit();
                                }}
                                control={control}
                                name={'allowEngageSms'}
                            />
                        </div>
                        <Separator className={'!-mx-6 !w-auto'} />
                        <div className={'flex items-center justify-between'}>
                            <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                    <Form.Label
                                        className="text-md"
                                        name={path('allowEngageAddress')}
                                    >
                                        Direct mail
                                    </Form.Label>
                                    {contact.address_unsubscribed_at && (
                                        <Badge color="warning">
                                            <FontAwesomeIcon
                                                className="text-orange-400"
                                                icon={faBellSlash}
                                            />
                                            <span>
                                                Unsubscribed on{' '}
                                                {isoDateFromServer(
                                                    contact.address_unsubscribed_at,
                                                ).toFormat('MMMM dd, yyyy')}
                                            </span>
                                        </Badge>
                                    )}
                                </div>
                                <div className="italic text-gray-600">
                                    {contact?.primary_address ? (
                                        inlineAddress(contact?.primary_address)
                                    ) : (
                                        <Button
                                            variant="none"
                                            color="primary"
                                            className="font-medium"
                                            onClick={() =>
                                                history.push(`${location.pathname}/addresses/new`)
                                            }
                                        >
                                            + Add Address
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <Form.Switch
                                disabled={isPending}
                                onCheckedChange={() => {
                                    submit();
                                }}
                                control={control}
                                name={'allowEngageAddress'}
                            />
                        </div>
                    </div>
                </form>
            </Form.Provider>
        </Box>
    );
};

export { ContactCommunicationPreferences };
